<template>
  <section v-if="experiences.length" class="experience-listing">
    <h2 class="title">{{ t('title') }}</h2>
    <div v-if="showSkeletons" class="list-wrap">
      <ExpCardSkeleton v-for="i in skeletonsCount" :key="`exp-card-skeleton-${i + 1}`" />
    </div>
    <div v-else class="list-wrap">
      <ExpCard
        v-for="exp in experiences"
        :key="exp.exp.id"
        :exp="exp.exp"
        :show-location="showLocation"
        :is-in-favorites="exp.favorite"
        :sogu-page="analyticEventsPrefix"
        :tooltip-teleport-target="modalTarget"
        class="exp-list-item-wrapper"
        target-blank
        @toggle-favorite="toggleFavorite(exp)"
        @click="handleExpClick(exp.exp)"
      />
    </div>
  </section>

  <div v-if="moreButton" class="exp-button">
    <UiButton
      data-test="article-exp-btn-show-all"
      class="btn"
      :variant="UiButtonVariant.OUTLINE"
      :color-mode="UiButtonColorMode.NEUTRAL"
      :text="moreButtonText"
      :is-loading="moreButtonLoading"
      :href="moreButtonLink"
      @click.prevent="handleButtonClick"
    />
  </div>
</template>

<script lang="ts" setup>
import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
import { ExpCard, ExpCardSkeleton } from '@experience/exp-card';
import { useI18n } from '@experience/i18n';
import { computed, inject } from 'vue';

import type { Experience, ExperienceWithFavorite } from '@/entities/experience';

import ru from '../i18n/ru.json';

interface Props {
  /**
   * Список экскурсий с флагом "в избранном".
   */
  experiences: ExperienceWithFavorite[];

  /**
   * Флаг загрузки данных.
   *
   * @default false
   */
  loading?: boolean;

  /**
   * Количество скелетонов.
   *
   * @default 3
   */
  skeletonsCount?: number;

  /**
   * Отображать кнопку "показать еще".
   *
   * @default false
   */
  moreButton?: boolean;

  /**
   * Текст кнопки "показать еще".
   *
   * @default ""
   */
  moreButtonText?: string;

  /**
   * Ссылка, указываемая в кнопке "показать еще".
   */
  moreButtonLink?: string;

  /**
   * Индикатор загрузки на кнопке "показать еще" (UiButtonV3: is-loading).
   *
   * @default false
   */
  moreButtonLoading?: boolean;

  /**
   * Префикс для аналитических событий карточки.
   */
  analyticEventsPrefix: string;

  /**
   * Флаг отображения локации
   */
  showLocation?: boolean;

  /**
   * geo объект
   */
  geoSubject?: {
    id: number;
    name: string;
  };

  /**
   * geo тип
   */
  geoType?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  skeletonsCount: 3,
  moreButton: false,
  moreButtonText: '',
  moreButtonLoading: false,
});

const emit = defineEmits<{
  /**
   * Событие нажатия кнопки "еще".
   */
  'load-more': [];

  /**
   * Событие клика по карточке экскурсии.
   */
  'card-click': [exp: Experience];

  /**
   * Событие добавления экскурсии в избранное.
   */
  'set-favorite': [exp: Experience];

  /**
   * Событие удаления экскурсии из избранного.
   */
  'unset-favorite': [id: number];
}>();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
const modalTarget = (shadowRoot?.querySelector('.modal-target') ?? undefined) as HTMLElement | undefined;

const showSkeletons = computed(() => props.loading);

const handleButtonClick = () => {
  emit('load-more');
};

/**
 * Переключает состояние избранного для карточки экскурсии.
 */
const toggleFavorite = (experienceWithFavorite: ExperienceWithFavorite) => {
  const { exp, favorite } = experienceWithFavorite;
  const { id } = exp;

  if (favorite) {
    emit('unset-favorite', id);
  } else {
    emit('set-favorite', exp);
  }
};

const handleExpClick = (exp: Experience) => {
  emit('card-click', exp);
};
</script>

<style lang="scss" scoped>
.experience-listing {
  position: relative;
  margin: 80px 0 auto;

  @include tablet {
    margin: 56px 0 auto;
  }

  @include mobile {
    margin: 36px 0 auto;
  }
}

.list-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-top: 16px;

  @supports (display: grid) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 40px 24px;

    @include desktop-xs-listing {
      grid-template-columns: repeat(auto-fit, minmax(300px, 0.333fr));
    }

    @include tablet {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @include mobile {
      grid-template-columns: minmax(0, 1fr);
      gap: 26px 24px;
    }
  }
}

.exp-button {
  display: flex;
  justify-content: center;
  margin: 56px 0 0;

  @include tablet {
    margin: 28px 0 0;
  }
}

.title {
  @include base-800-750-extra;

  margin-bottom: 24px;

  @include mobile {
    @include base-700-500-extra;

    margin-bottom: 20px;
  }
}
</style>
